import React, { useState } from "react";
import { apiFetch } from "../utils/wkCommon";
import { isZh } from "../utils/language";

export default function UploadDialog({ user, currentFolder, onClose, onUploaded }) {

  const [progress, setProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState("idle"); 

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [titleZh, setTitleZh] = useState("");
  const [titleId, setTitleId] = useState("");

  function uploadToR2(uploadUrl, file) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open("PUT", uploadUrl);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setProgress(percent);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error("Upload failed"));
        }
      };

      xhr.onerror = () => reject(new Error("Network error"));

      //xhr.setRequestHeader("Content-Type", file.type);
      xhr.send(file);
    });
  }

  function extractZh(text) {
    const match = text.match(/[\u4e00-\u9fff]+/g);
    return match ? match.join("") : "";
  }

  function extractLatin(text) {
    const match = text.match(/[A-Za-z0-9]+/g);
    return match ? match.join("") : "";
  }

  function removeExtension(filename) {
    return filename.replace(/\.[^/.]+$/, "");
  }

  async function upload() {
    // 🚫 GUARD: tidak boleh upload ke root
    if (!currentFolder) {
      alert("Upload is not allowed in root folder.");
      return;
    }

    if (!file) return;

    try {
      setUploading(true);
      setProgress(0);

      // 1️⃣ PRESIGN
      setUploadPhase("presign");

      const presign = await apiFetch("/api/upload-presign", {
        method: "POST",
        body: {
          filename: file.name,
          mime: file.type,
          size: file.size,
        },
      });

      if (!presign?.uploadUrl || !presign?.objectKey || !presign?.bucket) {
        throw new Error("Invalid presign response");
      }
      
      // 2️⃣ UPLOAD TO R2
      setUploadPhase("uploading");

      await uploadToR2(presign.uploadUrl, file);

      // 3️⃣ SAVE METADATA
      setUploadPhase("saving");

      await apiFetch("/api/document-files", {
        method: "POST",
        body: {
          data: {
            title_zh: titleZh,
            title_id: titleId,
            document_folder: currentFolder?.id || null,
            storage_provider: "cloudflare_r2",
            bucket: presign.bucket,
            object_key: presign.objectKey,
            mime_type: file.type,
            file_size: file.size,
            access_level: "public",
            recordOwner: user?.username || "system",
            recordEditor: user?.username || "system",
          },
        },
      });

      onUploaded();
      onClose();

    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setUploading(false);
      setUploadPhase("idle");
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content upload-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{isZh() ? "上傳檔案" : "Upload File"}</h3>

        <div
          className={`drop-zone ${file ? "has-file" : ""}`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const dropped = e.dataTransfer.files?.[0];
            if (!dropped) return;

            setFile(dropped);

            const baseName = removeExtension(dropped.name);

            const zh = extractZh(baseName);
            const id = extractLatin(baseName);

            setTitleZh(zh || baseName);
            setTitleId(id || baseName);
          }}
          onClick={() => document.getElementById("fileInput").click()}
        >
          {file ? (
            <div className="file-preview">
              📄 {file.name}
            </div>
          ) : (
            <div className="drop-text">
              {isZh()
                ? "拖曳檔案到這裡或點擊選擇"
                : "Drag file here or click to browse"}
            </div>
          )}
        </div>

        {uploading && (
          <div className="upload-progress">
            <div className="progress-bar-wrapper">
              <div
                className="progress-bar-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="progress-text">
              {uploadPhase === "presign" && "Preparing upload..."}
              {uploadPhase === "uploading" && `Uploading... ${progress}%`}
              {uploadPhase === "saving" && "Saving document..."}
            </div>
          </div>
        )}

        <input
          id="fileInput"
          type="file"
          hidden
          onChange={(e) => {
            const selected = e.target.files[0];
            if (!selected) return;

            setFile(selected);

            const baseName = removeExtension(selected.name);

            const zh = extractZh(baseName);
            const id = extractLatin(baseName);

            setTitleZh(zh || baseName);
            setTitleId(id || baseName);
          }}
        />
        <br/>
        <div className="form-row">
          <div className="form-group">
            <label>
              {isZh() ? "中文標題" : "Chinese Title"}
            </label>
            <input
              placeholder={isZh() ? "請輸入中文標題" : "Enter Chinese title"}
              value={titleZh}
              onChange={(e) => setTitleZh(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>
              {isZh() ? "印尼文標題" : "Judul Indonesia"}
            </label>
            <input
              placeholder={isZh() ? "請輸入印尼文標題" : "Enter Indonesian title"}
              value={titleId}
              onChange={(e) => setTitleId(e.target.value)}
            />
          </div>
        </div>

        <div className="dialog-actions">
          <button onClick={onClose}>
            {isZh() ? "取消" : "Cancel"}
          </button>
          <button
            className="btn-primary"
            disabled={!file || !titleZh || !titleId || uploading}
            onClick={upload}
          >
            {isZh() ? "上傳" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );

}
